import { useCallback, useRef, type KeyboardEvent } from 'react'

/**
 * Arrow-key navigation for a `role="radiogroup"` built from buttons.
 *
 * Declaring the role is a promise about behaviour: a screen-reader user reaches
 * for the arrow keys, and Tab-through-each-option is not what the role says
 * happens. This supplies the standard pattern — arrows move and select,
 * Home/End jump to the ends, and a **roving tabindex** keeps the whole group to
 * a single Tab stop instead of one per option.
 *
 * The controls are buttons rather than native radios because a native radio
 * cannot carry the track's own labels or the third, unmeasured state. This is
 * the cost of that, and it is smaller than the cost of a control that cannot
 * express "not measured".
 */
export interface RadioGroupKeys {
  /**
   * Attach to each **option**, not to the group.
   *
   * The group must not be focusable — it would be an extra tab stop, which is
   * exactly what the roving tabindex below exists to avoid. Key events fire on
   * whichever option has focus, so handling them there is both correct and what
   * `jsx-a11y/interactive-supports-focus` is asking for.
   */
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
  /** Attach to each option's ref. */
  readonly register: (index: number) => (node: HTMLButtonElement | null) => void
  /** `tabIndex` for the option at this index. */
  readonly tabIndexFor: (index: number) => 0 | -1
}

export function useRadioGroupKeys(
  count: number,
  selectedIndex: number,
  onSelect: (index: number) => void,
): RadioGroupKeys {
  const options = useRef<(HTMLButtonElement | null)[]>([])

  const register = useCallback(
    (index: number) => (node: HTMLButtonElement | null) => {
      options.current[index] = node
    },
    [],
  )

  const focusAndSelect = useCallback(
    (index: number) => {
      onSelect(index)
      options.current[index]?.focus()
    },
    [onSelect],
  )

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (count === 0) {
        return
      }
      // With nothing selected, the first arrow press starts at the beginning
      // rather than wrapping from a phantom position.
      const from = selectedIndex < 0 ? 0 : selectedIndex

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault()
          focusAndSelect((from + 1) % count)
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault()
          focusAndSelect((from - 1 + count) % count)
          break
        case 'Home':
          event.preventDefault()
          focusAndSelect(0)
          break
        case 'End':
          event.preventDefault()
          focusAndSelect(count - 1)
          break
        default:
          break
      }
    },
    [count, selectedIndex, focusAndSelect],
  )

  /**
   * One tab stop for the group: the selected option, or the first when nothing
   * is selected. Without this every option is its own stop, and a ten-option
   * enum costs ten presses to tab past.
   */
  const tabIndexFor = useCallback(
    (index: number): 0 | -1 => {
      const active = selectedIndex < 0 ? 0 : selectedIndex
      return index === active ? 0 : -1
    },
    [selectedIndex],
  )

  return { onKeyDown, register, tabIndexFor }
}
